import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Drawer, Pagination } from "antd";
import Avatar from "../../../componentes/ui/Avatar";
import verifiedBadge from "../../../assets/verified.png";
import { formatDate } from "../../../utils/dateFormat";
import { formatNumber } from "../../../utils/numberFormat";
import communityAPI from "../../../api/community";

const defaultSkills = [];
const FOLLOW_LIST_PAGE_SIZE = 12;

const extractFollowEntries = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  if (Array.isArray(payload.items)) {
    return payload.items;
  }
  return [];
};

const resolveFollowTotal = (payload, fallbackLength = 0) => {
  if (!payload) {
    return fallbackLength;
  }
  if (typeof payload.count === "number") {
    return payload.count;
  }
  if (typeof payload.total === "number") {
    return payload.total;
  }
  return fallbackLength;
};

const formatFollowedAt = (value) => {
  if (!value) {
    return "Recently";
  }
  try {
    return formatDate(value);
  } catch (err) {
    return value;
  }
};

export default function AuthorSpotlight({
  authorName,
  username,
  avatar,
  headline,
  bio,
  emptyBioMessage = "This member hasn't shared a bio yet.",
  skills = defaultSkills,
  followerCount = 0,
  followerLabel = "Followers",
  followingCount = 0,
  followingLabel = "Following",
  joinedDate,
  showFollowButton = false,
  onFollowToggle,
  followLoading = false,
  isFollowing = false,
  followButtonLabels = { follow: "Follow", following: "Following" },
  profileLink,
  selfNotice,
  isTrusted = false,
}) {
  const hasSkills = Array.isArray(skills) && skills.length > 0;
  const showFollowCta =
    showFollowButton && typeof onFollowToggle === "function";
  const normalizedHeadline = headline?.trim();
  const normalizedBio = bio?.trim();
  const [followDrawerOpen, setFollowDrawerOpen] = useState(false);
  const [followDrawerMode, setFollowDrawerMode] = useState(null);
  const [followListData, setFollowListData] = useState([]);
  const [followListTotal, setFollowListTotal] = useState(0);
  const [followListLoading, setFollowListLoading] = useState(false);
  const [followListPage, setFollowListPage] = useState(1);
  const canOpenFollowDrawer = Boolean(username);
  const statButtonStateClass = canOpenFollowDrawer
    ? "hover:text-primary dark:hover:text-primary/80"
    : "cursor-not-allowed opacity-60";
  const followDrawerTitle =
    followDrawerMode === "following" ? "Following" : "Followers";

  const closeFollowDrawer = () => {
    setFollowDrawerOpen(false);
    setFollowDrawerMode(null);
    setFollowListPage(1);
    setFollowListData([]);
    setFollowListTotal(0);
  };

  const handleStatClick = (mode) => {
    if (!canOpenFollowDrawer || !mode) {
      return;
    }
    setFollowListPage(1);
    setFollowDrawerMode(mode);
    setFollowDrawerOpen(true);
  };

  useEffect(() => {
    if (!followDrawerOpen || !followDrawerMode || !username) {
      return;
    }
    let cancelled = false;
    const fetchFollowEntries = async () => {
      setFollowListLoading(true);
      try {
        const requester =
          followDrawerMode === "followers"
            ? communityAPI.users.getFollowers
            : communityAPI.users.getFollowing;
        const { data } = await requester(username, {
          page: followListPage,
          page_size: FOLLOW_LIST_PAGE_SIZE,
        });
        if (cancelled) {
          return;
        }
        const entries = extractFollowEntries(data);
        setFollowListData(entries);
        setFollowListTotal(resolveFollowTotal(data, entries.length));
      } catch (err) {
        console.error("Failed to load follow entries", err);
        if (!cancelled) {
          setFollowListData([]);
          setFollowListTotal(0);
        }
      } finally {
        if (!cancelled) {
          setFollowListLoading(false);
        }
      }
    };
    fetchFollowEntries();
    return () => {
      cancelled = true;
    };
  }, [followDrawerOpen, followDrawerMode, followListPage, username]);

  return (
    <>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-6">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-4">
            <Avatar
              name={authorName}
              size={16}
              src={avatar}
              className="border-2 border-white dark:border-gray-900 shadow-sm"
            />
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Written by
              </p>
              <div className="flex items-center gap-2">
                {profileLink ? (
                  <Link
                    to={profileLink}
                    className="text-xl font-semibold text-gray-900 dark:text-white hover:text-primary dark:hover:text-primary/80 transition-colors flex items-center gap-1.5"
                  >
                    {authorName}
                    {isTrusted && (
                      <img
                        src={verifiedBadge}
                        alt="Verified"
                        className="w-4 h-4 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                      />
                    )}
                  </Link>
                ) : (
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                    {authorName}
                    {isTrusted && (
                      <img
                        src={verifiedBadge}
                        alt="Verified"
                        className="w-4 h-4 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                      />
                    )}
                  </h3>
                )}
              </div>
              {normalizedHeadline && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 font-medium">
                  {normalizedHeadline}
                </p>
              )}
            </div>
          </div>

          {/* Follow Button - Desktop */}
          {showFollowCta && (
            <button
              onClick={onFollowToggle}
              disabled={followLoading}
              className={`hidden sm:inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
                isFollowing
                  ? "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600"
                  : "bg-primary text-white hover:bg-primary/90"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isFollowing
                ? followButtonLabels.following
                : followButtonLabels.follow}
              {followLoading && (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              )}
            </button>
          )}
        </div>

        {/* Bio Section */}
        {normalizedBio && (
          <div className="mb-6">
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
              {normalizedBio}
            </p>
          </div>
        )}

        {/* Skills Section */}
        {hasSkills && (
          <div className="mb-6">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Skills & Expertise
            </p>
            <div className="flex flex-wrap gap-2">
              {skills.slice(0, 6).map((skill, index) => (
                <span
                  key={`${skill}-${index}`}
                  className="px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stats & Info Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={() => handleStatClick("followers")}
              disabled={!canOpenFollowDrawer}
              className={`text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-lg px-1 ${statButtonStateClass}`}
            >
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatNumber(followerCount)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {followerLabel}
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleStatClick("following")}
              disabled={!canOpenFollowDrawer}
              className={`text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-lg px-1 ${statButtonStateClass}`}
            >
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatNumber(followingCount)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {followingLabel}
              </p>
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {joinedDate && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Joined {formatDate(joinedDate)}
              </div>
            )}

            {/* Mobile Follow Button & Self Notice */}
            <div>
              {showFollowCta ? (
                <button
                  onClick={onFollowToggle}
                  disabled={followLoading}
                  className={`sm:hidden w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
                    isFollowing
                      ? "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600"
                      : "bg-primary text-white hover:bg-primary/90"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isFollowing
                    ? followButtonLabels.following
                    : followButtonLabels.follow}
                  {followLoading && (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  )}
                </button>
              ) : (
                selfNotice && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {selfNotice}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
      
      <Drawer
        title={`${followDrawerTitle} · ${authorName}`}
        placement="right"
        width={420}
        open={followDrawerOpen}
        onClose={closeFollowDrawer}
        destroyOnHidden
      >
        {followListLoading ? (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            Loading {followDrawerTitle.toLowerCase()}...
          </div>
        ) : followListData.length ? (
          <div className="space-y-3">
            {followListData.map((entry) => (
              <div
                key={entry.username || entry.id}
                className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white/80 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/70"
              >
                <Avatar
                  src={entry.avatar_url || entry.avatar}
                  name={entry.name || entry.username}
                  size={10}
                  className="border border-white dark:border-gray-800"
                />
                <div className="flex-1 min-w-0">
                  {entry.username ? (
                    <Link
                      to={`/profile/${entry.username}`}
                      className="font-semibold text-gray-900 hover:text-primary dark:text-white flex items-center gap-1.5"
                    >
                      {entry.name || entry.username}
                      {(entry.is_trusted || entry.status?.is_trusted) && (
                        <img
                          src={verifiedBadge}
                          alt="Verified"
                          className="w-3.5 h-3.5 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                        />
                      )}
                    </Link>
                  ) : (
                    <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                      {entry.name || "Community Member"}
                      {(entry.is_trusted || entry.status?.is_trusted) && (
                        <img
                          src={verifiedBadge}
                          alt="Verified"
                          className="w-3.5 h-3.5 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                        />
                      )}
                    </p>
                  )}
                  {entry.followed_at && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {followDrawerMode === "followers"
                        ? `Following since ${formatFollowedAt(entry.followed_at)}`
                        : `Followed on ${formatFollowedAt(entry.followed_at)}`}
                    </p>
                  )}
                </div>
                {entry.username && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    @{entry.username}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            {followDrawerMode === "following"
              ? "Not following anyone yet."
              : "No followers yet."}
          </div>
        )}

        {followListTotal > FOLLOW_LIST_PAGE_SIZE && (
          <div className="mt-6 flex justify-center">
            <Pagination
              simple
              current={followListPage}
              pageSize={FOLLOW_LIST_PAGE_SIZE}
              total={followListTotal}
              onChange={(page) => setFollowListPage(page)}
              className="antd-pagination"
            />
          </div>
        )}
      </Drawer>
    </>
  );
}

AuthorSpotlight.propTypes = {
  authorName: PropTypes.string.isRequired,
  username: PropTypes.string,
  avatar: PropTypes.string,
  headline: PropTypes.string,
  bio: PropTypes.string,
  emptyBioMessage: PropTypes.string,
  skills: PropTypes.arrayOf(PropTypes.string),
  followerCount: PropTypes.number,
  followerLabel: PropTypes.string,
  followingCount: PropTypes.number,
  followingLabel: PropTypes.string,
  joinedDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  showFollowButton: PropTypes.bool,
  onFollowToggle: PropTypes.func,
  followLoading: PropTypes.bool,
  isFollowing: PropTypes.bool,
  followButtonLabels: PropTypes.shape({
    follow: PropTypes.string,
    following: PropTypes.string,
  }),
  profileLink: PropTypes.string,
  selfNotice: PropTypes.string,
};
